import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { vi } from "date-fns/locale";
import { Clock, CheckCircle2, X, Paperclip, Loader2, FileText, Upload } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";

export const Route = createFileRoute("/student/assignments")({ component: AssignmentsPage });

function AssignmentsPage() {
  const { user } = useAuth();
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [submittingId, setSubmittingId] = React.useState<string | null>(null);
  const [content, setContent] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);

  const [filterClass, setFilterClass] = React.useState("all");
  const [filterStatus, setFilterStatus] = React.useState("all");

  React.useEffect(() => { setFile(null); setContent(""); }, [submittingId]);

  const dataQ = useQuery({
    queryKey: ["student-assignments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: enrolls } = await supabase.from("enrollments").select("class_id").eq("student_id", user!.id).eq("is_active", true);
      const classIds = (enrolls ?? []).map(e => e.class_id);
      if (classIds.length === 0) return [];
      const { data: assigns, error } = await supabase
        .from("assignments")
        .select("id, title, description, max_score, due_at, class_id")
        .in("class_id", classIds)
        .order("due_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      const { data: subs } = await supabase.from("submissions")
        .select("id, assignment_id, score, feedback, submitted_at, file_url, file_name")
        .eq("student_id", user!.id);
      const subMap = Object.fromEntries((subs ?? []).map(s => [s.assignment_id, s]));
      const { data: classes } = await supabase.from("classes").select("id, name").in("id", classIds);
      const classMap = Object.fromEntries((classes ?? []).map(c => [c.id, c.name]));
      return (assigns ?? []).map(a => ({ ...a, className: classMap[a.class_id], submission: subMap[a.id] }));
    },
  });

  const classOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    (dataQ.data ?? []).forEach(a => { if (a.className) map.set(a.class_id, a.className); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [dataQ.data]);

  const filtered = React.useMemo(() => {
    let list = dataQ.data ?? [];
    if (filterClass !== "all") list = list.filter(a => a.class_id === filterClass);
    if (filterStatus === "pending") list = list.filter(a => !a.submission);
    if (filterStatus === "submitted") list = list.filter(a => a.submission && a.submission.score === null);
    if (filterStatus === "graded") list = list.filter(a => a.submission && a.submission.score !== null);
    return list;
  }, [dataQ.data, filterClass, filterStatus]);

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!submittingId || !user) return;
      if (file && file.size > 10 * 1024 * 1024) throw new Error("File quá lớn (tối đa 10MB)");

      let fileUrl: string | null = null;
      let fileName: string | null = null;

      if (file) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${user.id}/${submittingId}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("submissions")
          .upload(path, file, { upsert: true, contentType: file.type });
        if (upErr) throw upErr;
        const { data: signed, error: signErr } = await supabase.storage
          .from("submissions")
          .createSignedUrl(path, 60 * 60 * 24 * 30);
        if (signErr) throw signErr;
        fileUrl = signed?.signedUrl ?? null;
        fileName = file.name;
      }

      const { error } = await supabase.from("submissions").upsert(
        {
          assignment_id: submittingId,
          student_id: user.id,
          content: content.trim() || null,
          file_url: fileUrl,
          file_name: fileName,
        },
        { onConflict: "assignment_id,student_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã nộp bài thành công!");
      setSubmittingId(null);
      qc.invalidateQueries({ queryKey: ["student-assignments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <PortalLayout portal="student" title="Bài tập của tôi" breadcrumb={[{ label: "Học sinh", to: "/student" }, { label: "Bài tập" }]}>
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả lớp</SelectItem>
            {classOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả trạng thái</SelectItem>
            <SelectItem value="pending">Chưa nộp</SelectItem>
            <SelectItem value="submitted">Đã nộp · Chờ chấm</SelectItem>
            <SelectItem value="graded">Đã chấm điểm</SelectItem>
          </SelectContent>
        </Select>

        {filterClass !== "all" && (
          <Badge variant="secondary" className="text-xs self-center">
            {classOptions.find(c => c.id === filterClass)?.name}
          </Badge>
        )}
      </div>

      {dataQ.isLoading ? <div className="text-sm text-muted-foreground">Đang tải...</div> :
       filtered.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="size-8 mx-auto mb-2 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            {filterStatus === "pending" ? "Bạn đã nộp hết bài tập rồi!"
              : (dataQ.data?.length ?? 0) === 0 ? "Bạn chưa có bài tập nào."
              : "Không có bài tập phù hợp với bộ lọc."}
          </div>
        </div>
       ) : (
        <div className="space-y-4">
          {filtered.map((it, i) => {
            const sub = it.submission;
            const urgent = it.due_at && !sub && new Date(it.due_at).getTime() - Date.now() < 1000 * 60 * 60 * 24;
            const overdue = it.due_at && new Date(it.due_at) < new Date();
            return (
              <motion.div key={it.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-card border rounded-2xl p-5 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start gap-4">
                  <FileText className="size-7 shrink-0 mt-1" style={{ color: "var(--portal)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="font-semibold leading-tight">{it.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">Lớp: {it.className}</div>
                      </div>
                      {!sub && <Button size="sm" disabled={!canMutate} onClick={() => setSubmittingId(it.id)} style={{ background: "var(--portal)", color: "white" }}>{overdue ? "Nộp muộn" : "Nộp bài"}</Button>}
                      {sub && sub.score === null && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">Chờ chấm</span>}
                      {sub && sub.score !== null && (
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Điểm</div>
                          <div className="font-display text-2xl font-semibold" style={{ color: "var(--portal)" }}>{sub.score}/{it.max_score}</div>
                        </div>
                      )}
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs mb-2 ${urgent ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
                      <Clock className="size-3.5" />
                      {it.due_at ? `Hạn: ${format(new Date(it.due_at), "HH:mm dd/MM/yyyy", { locale: vi })}` : "Không hạn"}
                      {sub && <span className="ml-2">· Đã nộp {formatDistanceToNow(new Date(sub.submitted_at), { locale: vi, addSuffix: true })}</span>}
                    </div>
                    {it.description && <p className="text-sm text-muted-foreground">{it.description}</p>}
                    {sub?.file_url && (
                      <a href={sub.file_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2 text-xs px-2.5 py-1 rounded-full border hover:bg-muted transition-colors">
                        <Paperclip className="size-3" /> {sub.file_name ?? "Xem file đính kèm"}
                      </a>
                    )}
                    {sub?.feedback && (
                      <div className="mt-3 p-3 rounded-lg" style={{ background: "var(--portal-soft)" }}>
                        <div className="text-xs font-medium mb-1">Nhận xét từ giáo viên:</div>
                        <div className="text-sm italic">"{sub.feedback}"</div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {submittingId && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !submitMut.isPending && setSubmittingId(null)} className="fixed inset-0 bg-black/50 z-50" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
              <div className="bg-card rounded-3xl p-7 max-w-md w-full shadow-2xl pointer-events-auto">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display text-xl font-semibold">Nộp bài tập</h3>
                  <button onClick={() => setSubmittingId(null)} disabled={submitMut.isPending}><X className="size-5" /></button>
                </div>

                <div
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[var(--portal)]", "bg-[var(--portal-soft)]"); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove("border-[var(--portal)]", "bg-[var(--portal-soft)]"); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("border-[var(--portal)]", "bg-[var(--portal-soft)]");
                    const f = e.dataTransfer.files[0];
                    if (f) setFile(f);
                  }}
                  className="border-2 border-dashed border-border rounded-xl p-5 text-center cursor-pointer transition-colors mb-3 hover:border-[var(--portal)] hover:bg-[var(--portal-soft)]"
                  onClick={() => document.getElementById("file-upload-input")?.click()}
                >
                  <input
                    id="file-upload-input"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                  {file ? (
                    <div className="flex items-center justify-center gap-2">
                      <Paperclip className="size-5" />
                      <div className="text-left">
                        <div className="text-sm font-medium">{file.name}</div>
                        <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                        className="ml-2 text-muted-foreground hover:text-destructive"
                      ><X className="size-4" /></button>
                    </div>
                  ) : (
                    <>
                      <Upload className="size-6 mx-auto mb-1" />
                      <div className="text-sm font-medium">Kéo file vào đây hoặc click để chọn</div>
                      <div className="text-xs text-muted-foreground mt-1">PDF, Word, JPG, PNG — tối đa 10MB</div>
                    </>
                  )}
                </div>

                <Textarea placeholder="Hoặc nhập nội dung bài làm / ghi chú..." rows={5} value={content} onChange={(e) => setContent(e.target.value)} className="mb-4" />
                <Button onClick={() => submitMut.mutate()} disabled={submitMut.isPending || (!file && !content.trim())} className="w-full gap-2" style={{ background: "var(--portal)", color: "white" }}>
                  {submitMut.isPending ? <><Loader2 className="size-4 animate-spin" />Đang nộp...</> : <><CheckCircle2 className="size-4" />Xác nhận nộp bài</>}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </PortalLayout>
  );
}
